import { useTheme } from "@emotion/react";
import { LockOutlined } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMicrospecDynamicUIContext } from "../GlobalProvider";
import { getOrDefault } from "../Helpers/Utils";
import { globalRateLimiter, PasswordSecurity } from "../Security/AuthSecurity";
import { SecurityMonitor, XSSProtection } from "../Security/XSSProtection";
import { useMicrospecThemeLanguageContext } from "../Themes";
import AuthWrapper from "./AuthWrapper";
import getEnabledCountryCodes from "./CountryCodesConstants";
import LoginEmailPassword from "./LoginEmailPassword";
import LoginEmailRegistration from "./LoginEmailRegistration";
import LoginIdentifierFirst from "./LoginIdentifierFirst";
import LoginPhoneOtp from "./LoginPhoneOtp";
import SupportedAuthProviders from "./MicrospecSupportedAuthProvider";
import ProfileCompletion from "./ProfileCompletion";

const Login = ({ config, registerMode=false, enableRegister=true }) => {
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirectTo')
  const context = useMicrospecDynamicUIContext();
  const { cloudProvider, security } = context;

  const application = getOrDefault(config.application, {});
  const auth = getOrDefault(application.auth, {});
  const enabledProviders = getOrDefault(auth.enabledProviders, []);
  const enabledCountryCodes = getEnabledCountryCodes(getOrDefault(auth.enabledCountryCodes, []));

  const { localizationProvider } = useMicrospecThemeLanguageContext()
  const signIn = localizationProvider.translateByType({
    type: "Internal",
    text: registerMode ? "signUp" : "signIn"
  })
  const { signInWithEmailAndPassword } = cloudProvider
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loginMethod, setLoginMethod] = useState(
    enabledProviders.length > 0 ? enabledProviders[0] : SupportedAuthProviders.EMAIL
  ) // Use first enabled provider or default to 'email'
  const [identifier, setIdentifier] = useState('')

  // Step management
  const [currentStep, setCurrentStep] = useState('identifier') // 'identifier', 'password', 'otp', 'profile', 'register'
  const [userInfo, setUserInfo] = useState(null)

  // Password step state
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)

  // Registration step state
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // OTP step state
  const [otp, setOtp] = useState('')
  const [countryCode, setCountryCode] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const [confirmationResult, setConfirmationResult] = useState(null)
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null)

  // Get theme for gradient colors and mode
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  // Generate gradients from theme colors based on mode
  const primaryGradient = isDarkMode
    ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
    : `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`;

  // Set up reCAPTCHA when component mounts and cleanup when step changes
  useEffect(() => {
    const setupRecaptcha = async () => {
      try {
        // Only setup if we need phone authentication and don't have a verifier
        if ((currentStep === 'otp' || loginMethod === SupportedAuthProviders.PHONE) && !recaptchaVerifier) {
          const verifier = await cloudProvider.userProfile.methods.setupRecaptcha('recaptcha-container');
          setRecaptchaVerifier(verifier);
        }
        // Don't cleanup immediately - let it persist for phone authentication
      } catch (error) {
        console.error('Error setting up reCAPTCHA:', error);
      }
    };

    setupRecaptcha();
  }, [currentStep, recaptchaVerifier, cloudProvider, loginMethod]);

  // Cleanup reCAPTCHA when component unmounts or when navigating away
  useEffect(() => {
    return () => {
      // Cleanup reCAPTCHA verifier when component unmounts
      if (cloudProvider?.userProfile?.methods?.cleanupRecaptcha && recaptchaVerifier) {
        try {
          console.log('Cleaning up reCAPTCHA on component unmount');
          cloudProvider.userProfile.methods.cleanupRecaptcha();
        } catch (error) {
          console.error('Error cleaning up reCAPTCHA:', error);
        }
      }
    };
  }, [cloudProvider, recaptchaVerifier]);


  const handleSubmit = async (event, errorData) => {
    // This function now handles errors from the identifier step
    setLoading(false); // Reset loading state
    if (errorData && errorData.error) {
      setError(errorData.error);
    }
  };

  const handleMethodSelect = (method) => {
    setLoginMethod(method);
    setIdentifier('');
    setError(false);
  };

  const handleIdentifierChange = (e) => {
    setIdentifier(e.target.value);
    setError(false); // Clear error when user types
  };

  const handleStartLoading = () => {
    setLoading(true);
    setError(false);
  };

  const handleUserFound = async (userInfo, method) => {
    // If in register mode and user exists, show error
    if (registerMode) {
      setLoading(false);
      setError({
        message: localizationProvider.translateByType({
          type: "Internal",
          text: "userAlreadyExists"
        }) || 'This account already exists. Please sign in instead.',
        showLoginLink: true
      });
      return;
    }
    
    setUserInfo(userInfo);
    if (method === SupportedAuthProviders.EMAIL) {
      setLoading(false); // Stop loading
      setError(false); // Clear any errors
      setCurrentStep('password');
    } else {
      // For phone login, use Firebase native phone authentication
      try {
        const fullPhoneNumber = `${countryCode}${identifier.replace(/^0+/, '')}`;

        // Use existing reCAPTCHA verifier or wait for it to be created
        let verifier = recaptchaVerifier;
        let retryCount = 0;
        const maxRetries = 10;
        
        // Wait for reCAPTCHA verifier to be available (up to 5 seconds)
        while (!verifier && retryCount < maxRetries) {
          console.log('Waiting for reCAPTCHA verifier for existing user...', retryCount + 1);
          await new Promise(resolve => setTimeout(resolve, 500));
          verifier = recaptchaVerifier;
          retryCount++;
        }

        if (!verifier) {
          console.log('Creating new reCAPTCHA verifier for existing user...');
          verifier = await cloudProvider.userProfile.methods.setupRecaptcha('recaptcha-container');
          setRecaptchaVerifier(verifier);
          
          // Add a small delay to ensure reCAPTCHA is fully initialized
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

        // Send OTP using Firebase phone authentication
        const result = await cloudProvider.userProfile.methods.sendPhoneOtp(fullPhoneNumber, verifier);

        if (result && result.success) {
          setConfirmationResult(result.confirmationResult);
          setLoading(false); // Stop loading
          setError(false); // Clear any errors
          setCurrentStep('otp');
          console.log('OTP sent successfully via Firebase');
        } else {
          setLoading(false);
          setError(localizationProvider.translateByType({
            type: "Internal",
            text: "failedToSendOtp"
          }) || 'Failed to send OTP. Please try again.');
        }
      } catch (error) {
        setLoading(false);
        setError(error.message || localizationProvider.translateByType({
          type: "Internal", 
          text: "failedToSendOtp"
        }) || 'Failed to send OTP. Please try again.');
        console.error('Error sending OTP via Firebase:', error);
      }
    }
  };

  const handleUserNotFound = async () => {
    // If in login mode and user doesn't exist, show error
    if (!registerMode) {
      setLoading(false);
      setError({
        message: localizationProvider.translateByType({
          type: "Internal",
          text: "userNotFoundPleaseRegister"
        }) || 'No account found with this information. Please register first.',
        showRegisterLink: true
      });
      return;
    }
    
    // For new users, we need to verify their phone number first to get UID
    // Then show profile completion
    if (loginMethod === SupportedAuthProviders.PHONE) {
      try {
        const fullPhoneNumber = `${countryCode}${identifier.replace(/^0+/, '')}`;

        console.log('Setting up authentication for new user with phone:', fullPhoneNumber);

        // Use existing reCAPTCHA verifier or wait for it to be created
        let verifier = recaptchaVerifier;
        let retryCount = 0;
        const maxRetries = 10;
        
        // Wait for reCAPTCHA verifier to be available (up to 5 seconds)
        while (!verifier && retryCount < maxRetries) {
          console.log('Waiting for reCAPTCHA verifier...', retryCount + 1);
          await new Promise(resolve => setTimeout(resolve, 500));
          verifier = recaptchaVerifier;
          retryCount++;
        }

        if (!verifier) {
          console.log('Creating new reCAPTCHA verifier for new user...');
          verifier = await cloudProvider.userProfile.methods.setupRecaptcha('recaptcha-container');
          setRecaptchaVerifier(verifier);
          
          // Add a small delay to ensure reCAPTCHA is fully initialized
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

        console.log('Sending OTP for new user with phone:', fullPhoneNumber);

        // Send OTP using Firebase phone authentication
        const result = await cloudProvider.userProfile.methods.sendPhoneOtp(fullPhoneNumber, verifier);

        if (result && result.success) {
          setConfirmationResult(result.confirmationResult);
          // Mark this as a new user registration
          setUserInfo({ isNewUser: true });
          setLoading(false);
          setError(false);
          setCurrentStep('otp');
          console.log('OTP sent for new user verification');
        } else {
          setLoading(false);
          setError(localizationProvider.translateByType({
            type: "Internal",
            text: "failedToSendVerificationCode"
          }) || 'Failed to send verification code. Please try again.');
        }
      } catch (error) {
        setLoading(false);
        setError(error.message || localizationProvider.translateByType({
          type: "Internal",
          text: "failedToSendVerificationCode"
        }) || 'Failed to send verification code. Please try again.');
        console.error('Error sending OTP for new user:', error);
      }
    } else {
      // For email registration, show password registration step
      console.log('Setting up email registration for new user:', identifier);
      setUserInfo({ isNewUser: true, email: identifier });
      setLoading(false);
      setError(false);
      setCurrentStep('register');
      console.log('Showing registration step for email user');
    }
  };

  const handleProfileComplete = async (profileData) => {
    setLoading(true);
    setError(false);

    try {
      // At this point, user is already authenticated and we have their UID
      if (!userInfo || !userInfo.uid) {
        throw new Error(localizationProvider.translateByType({
          type: "Internal",
          text: "userAuthenticationMissing"
        }) || 'User authentication information missing');
      }

      console.log('Creating user profile with UID:', userInfo.uid);

      // Create user profile in Firestore using the authenticated user's UID
      const profileDataWithUID = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        additionalData: {
          ...profileData.additionalData,
          profileCompletedAt: new Date().toISOString(),
          registrationMethod: 'phone',
          phoneNumber: `${countryCode}${identifier.replace(/^0+/, '')}`
        },
        roles: []
      };

      // Use updateUserProfile with the authenticated user's UID
      const result = await cloudProvider.userProfile.methods.updateUserProfile(profileDataWithUID);

      if (result && result.success) {
        console.log('User profile created successfully');
        setLoading(false);
        // Profile is complete, user should now be fully registered
        // Firebase auth state will handle navigation
      } else {
        throw new Error(localizationProvider.translateByType({
          type: "Internal",
          text: "failedToCreateProfile"
        }) || 'Failed to create user profile');
      }
    } catch (error) {
      setLoading(false);
      setError(error.message || localizationProvider.translateByType({
        type: "Internal",
        text: "failedToCreateProfile"
      }) || 'Failed to create profile. Please try again.');
      console.error('Error creating user profile:', error);
    }
  };

  const handleSetCountryCode = (code) => {
    setCountryCode(code);
  };

  const handleBackToIdentifier = () => {
    setCurrentStep('identifier');
    setError(false);
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setOtp('');
    setUserInfo(null);
    setConfirmationResult(null);
    // Keep reCAPTCHA verifier for potential reuse
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(false);

    try {
      // Sanitize inputs
      const sanitizedIdentifier = XSSProtection.sanitizeInput(identifier);
      const sanitizedPassword = XSSProtection.sanitizeInput(password);

      // Check rate limiting
      const rateLimitCheck = globalRateLimiter.isRateLimited(sanitizedIdentifier, 'login');
      if (rateLimitCheck.blocked) {
        setError(rateLimitCheck.reason);
        setLoading(false);
        return;
      }

      // Record login attempt
      globalRateLimiter.recordAttempt(sanitizedIdentifier, 'login');

      // Report security context
      if (security) {
        security.reportViolation('LOGIN_ATTEMPT', 'User attempting login', {
          identifier: sanitizedIdentifier,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        });
      }

      const result = await signInWithEmailAndPassword(sanitizedIdentifier, sanitizedPassword, rememberMe);
      if (result) {
        // Record successful login
        globalRateLimiter.recordSuccess(sanitizedIdentifier, 'login');
      } else {
        setError(localizationProvider.translateByType({
          type: "Internal",
          text: "invalidCredentials"
        }) || "Invalid email or password");
      }
    } catch (err) {
      // Report security violation for failed login
      if (security) {
        security.reportViolation('LOGIN_FAILURE', err.message, {
          identifier: XSSProtection.sanitizeInput(identifier),
          error: err.code || 'UNKNOWN_ERROR'
        });
      }

      setError(err.message || localizationProvider.translateByType({
        type: "Internal",
        text: "loginError"
      }) || 'Login error occurred');
    }

    setLoading(false);
  };

  const handleRegistrationSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(false);

    try {
      // Sanitize inputs
      const sanitizedIdentifier = XSSProtection.sanitizeInput(identifier);
      const sanitizedPassword = XSSProtection.sanitizeInput(newPassword);

      // Check rate limiting
      const rateLimitCheck = globalRateLimiter.isRateLimited(sanitizedIdentifier, 'register');
      if (rateLimitCheck.blocked) {
        setError(rateLimitCheck.reason);
        setLoading(false);
        return;
      }

      // Record registration attempt
      globalRateLimiter.recordAttempt(sanitizedIdentifier, 'register');

      // Comprehensive password validation
      const passwordValidation = PasswordSecurity.validatePassword(sanitizedPassword, {
        email: sanitizedIdentifier,
        firstName: '',
        lastName: ''
      });

      if (!passwordValidation.valid) {
        setError(passwordValidation.errors[0]); // Show first error
        setLoading(false);
        return;
      }

      // Validate password confirmation
      if (sanitizedPassword !== confirmPassword) {
        setError(localizationProvider.translateByType({
          type: "Internal",
          text: "passwordsDoNotMatch"
        }) || 'كلمات المرور غير متطابقة');
        setLoading(false);
        return;
      }

      // Report security context
      if (security) {
        security.reportViolation('REGISTRATION_ATTEMPT', 'User attempting registration', {
          identifier: sanitizedIdentifier,
          passwordStrength: passwordValidation.strength,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        });
      }

      // Import createUserWithEmailAndPassword if not already imported
      const { createUserWithEmailAndPassword } = await import('firebase/auth');
      const firebaseAuth = cloudProvider?.auth;
      
      if (!firebaseAuth) {
        throw new Error('Firebase auth not available');
      }

      // Create user with email and password
      const userCredential = await createUserWithEmailAndPassword(
        firebaseAuth, 
        sanitizedIdentifier, 
        sanitizedPassword
      );
      
      console.log('Email user created successfully:', userCredential.user.uid);
      
      // Record successful registration
      globalRateLimiter.recordSuccess(sanitizedIdentifier, 'register');
      
      // Set user info and proceed to profile completion
      setUserInfo({ 
        uid: userCredential.user.uid, 
        isNewUser: true, 
        email: sanitizedIdentifier 
      });
      setCurrentStep('profile');
    } catch (error) {
      let errorMessage;
      
      // Report security violation for failed registration  
      SecurityMonitor.reportViolation(
        'REGISTRATION_FAILURE',
        error.message || 'Registration failed',
        {
          severity: 'HIGH',
          identifier: identifier || 'unknown', // Use original identifier as fallback
          error: error.code || 'UNKNOWN_ERROR',
          timestamp: new Date().toISOString()  
        }
      );
      
      // Handle Firebase auth errors
      if (error.code) {
        switch (error.code) {
          case 'auth/email-already-in-use':
            errorMessage = localizationProvider.translateByType({
              type: "Internal",
              text: "emailAlreadyInUse"
            }) || 'هذا البريد الإلكتروني مسجل بالفعل. يرجى تسجيل الدخول بدلاً من ذلك.';
            break;
          case 'auth/weak-password':
            errorMessage = localizationProvider.translateByType({
              type: "Internal",  
              text: "passwordTooShort"
            }) || 'كلمة المرور ضعيفة جداً. يرجى استخدام 8 أحرف على الأقل.';
            break;
          case 'auth/invalid-email':
            errorMessage = localizationProvider.translateByType({
              type: "Internal",
              text: "invalidEmail"
            }) || 'عنوان البريد الإلكتروني غير صالح.';
            break;
          default:
            errorMessage = error.message;
        }
      } else {
        errorMessage = error.message || localizationProvider.translateByType({
          type: "Internal",
          text: "registrationError"
        }) || 'حدث خطأ أثناء التسجيل. يرجى المحاولة مرة أخرى.';
      }
      
      setError(errorMessage);
      console.error('Error creating email user:', error);
    }

    setLoading(false);
  };

  const handleOtpSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(false);

    try {
      // Verify OTP using Firebase's native phone authentication
      if (!confirmationResult) {
        setLoading(false);
        setError(localizationProvider.translateByType({
          type: "Internal",
          text: "noConfirmationResult"
        }) || "No confirmation result found. Please try again.");
        return;
      }

      // Verify the OTP code with Firebase
      const result = await cloudProvider.userProfile.methods.verifyPhoneOtp(confirmationResult, otp);

      if (result && result.success) {
        console.log('Phone authentication successful via Firebase');

        // Check if this is a new user who needs to complete profile
        if (userInfo && userInfo.isNewUser) {
          console.log('New user authenticated, showing profile completion...');
          // Store the authenticated user info and show profile completion
          setUserInfo({
            ...userInfo,
            uid: result.user.uid,
            isAuthenticated: true
          });
          setLoading(false);
          setCurrentStep('profile');
          return;
        }

        // Existing user - authentication complete
        setLoading(false);
        // Firebase Auth will automatically handle the authentication and state change
        // Navigation will be handled by Firebase auth state change
      } else {
        setLoading(false);
        setError(localizationProvider.translateByType({
          type: "Internal",
          text: "invalidOtp"
        }) || "رمز التحقق غير صحيح");
      }
    } catch (err) {
      setLoading(false);
      setError(err.message || localizationProvider.translateByType({
        type: "Internal",
        text: "otpVerificationFailed"
      }) || "فشل في التحقق من الرمز");
      console.error('OTP verification error:', err);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    setError(false);

    try {
      // Create full phone number with country code
      const fullPhoneNumber = `${countryCode}${identifier.replace(/^0+/, '')}`;

      // Ensure reCAPTCHA is set up
      let verifier = recaptchaVerifier;
      if (!verifier) {
        verifier = await cloudProvider.userProfile.methods.setupRecaptcha('recaptcha-container');
        setRecaptchaVerifier(verifier);
      }

      // Resend OTP using Firebase phone authentication
      const result = await cloudProvider.userProfile.methods.sendPhoneOtp(fullPhoneNumber, verifier);

      if (result && result.success) {
        console.log('OTP resent successfully via Firebase');
        setConfirmationResult(result.confirmationResult);
      }

      setResendLoading(false);
    } catch (err) {
      setResendLoading(false);
      setError(err.message || localizationProvider.translateByType({
        type: "Internal",
        text: "failedToResendOtp"
      }) || "فشل في إعادة إرسال رمز التحقق");
      console.error('Error resending OTP:', err);
    }
  };

  return (
    <AuthWrapper key={Login.name} autoRedirect={true} redirectTo={redirectTo}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Avatar
          sx={{
            m: '0 auto',
            mb: 2,
            width: 56,
            height: 56,
            background: isDarkMode ? '#f0f0f0' : primaryGradient,
            boxShadow: isDarkMode 
              ? `0 8px 32px ${theme.palette.primary.dark}50`
              : `0 8px 32px ${theme.palette.primary.main}30`,
            border: isDarkMode ? `1px solid ${theme.palette.primary.main}30` : 'none'
          }}
        >
          <LockOutlined 
            sx={{
              fontSize: 28 
              }} />
        </Avatar>
        <Typography
          component="h1"
          variant="h4"
          sx={{
            fontWeight: 700,
            background: isDarkMode ? '#f0f0f0' : primaryGradient,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}
        >
          {currentStep === 'register' || registerMode ? (
            localizationProvider.translateByType({
              type: "Internal",
              text: "createNewAccount"
            })
          ) : signIn}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ 
            fontSize: '1.1rem',
            opacity: isDarkMode ? 0.9 : 1
          }}
        >
          {currentStep === 'identifier' && (
            localizationProvider.translateByType({
              type: "Internal",
              text: "selectLoginMethod"
            })
          )}
          {currentStep === 'password' && (
            localizationProvider.translateByType({
              type: "Internal",
              text: "enterYourPassword"
            })
          )}
          {currentStep === 'register' && (
            localizationProvider.translateByType({
              type: "Internal",
              text: "createNewPasswordDescription"
            })
          )}
          {currentStep === 'otp' && (
            localizationProvider.translateByType({
              type: "Internal",
              text: "enterVerificationCode"
            })
          )}
          {currentStep === 'profile' && (
            localizationProvider.translateByType({
              type: "Internal",
              text: "profileCompletionDescription"
            })
          )}
        </Typography>
      </Box>

      {currentStep === 'identifier' && (
        <LoginIdentifierFirst
          loginMethod={loginMethod}
          identifier={identifier}
          error={error}
          loading={loading}
          signIn={signIn}
          redirectTo={redirectTo}
          enabledProviders={enabledProviders}
          enableRegister={enableRegister}
          enabledCountryCodes={enabledCountryCodes}
          registerMode={registerMode}
          onMethodSelect={handleMethodSelect}
          onIdentifierChange={handleIdentifierChange}
          onSubmit={handleSubmit}
          onUserFound={handleUserFound}
          onUserNotFound={handleUserNotFound}
          onSetCountryCode={handleSetCountryCode}
          onStartLoading={handleStartLoading}
        />
      )}

      {currentStep === 'password' && (
        <LoginEmailPassword
          identifier={identifier}
          password={password}
          error={error}
          loading={loading}
          rememberMe={rememberMe}
          redirectTo={redirectTo}
          onPasswordChange={setPassword}
          onRememberMeChange={setRememberMe}
          onSubmit={handlePasswordSubmit}
          onBackToIdentifier={handleBackToIdentifier}
        />
      )}

      {currentStep === 'register' && (
        <LoginEmailRegistration
          identifier={identifier}
          newPassword={newPassword}
          confirmPassword={confirmPassword}
          error={error}
          loading={loading}
          onNewPasswordChange={setNewPassword}
          onConfirmPasswordChange={setConfirmPassword}
          onSubmit={handleRegistrationSubmit}
          onBackToIdentifier={handleBackToIdentifier}
        />
      )}

      {currentStep === 'otp' && (
        <LoginPhoneOtp
          identifier={identifier}
          countryCode={countryCode}
          otp={otp}
          error={error}
          loading={loading}
          resendLoading={resendLoading}
          redirectTo={redirectTo}
          onOtpChange={setOtp}
          onSubmit={handleOtpSubmit}
          onResendOtp={handleResendOtp}
          onBackToIdentifier={handleBackToIdentifier}
        />
      )}

      {currentStep === 'profile' && (
        <ProfileCompletion
          identifier={identifier}
          countryCode={countryCode}
          loading={loading}
          error={error}
          onProfileComplete={handleProfileComplete}
          onBackToIdentifier={handleBackToIdentifier}
        />
      )}

      {/* Hidden reCAPTCHA container for Firebase phone authentication */}
      <div id="recaptcha-container" style={{ display: 'none' }}></div>
    </AuthWrapper>
  );
};

export default Login;
